import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { AlertCircle, Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  useMassMigrationWizardIdValidation,
  useMassMigrationWizardExcludedRows,
  useMassMigrationWizardSelections,
  useMassMigrationWizardModuleValidation,
  useMassMigrationWizardParsedCsv,
  useMassMigrationWizardDetectedModules,
  useMassMigrationWizardActions,
} from '@/stores/massMigrationWizardStore';
import { usePreflightValidation, preflightKey } from '../hooks/usePreflightValidation';
import { WizardNavigation } from './WizardNavigation';
import { StatTile } from './StatTile';

// ─── Types ────────────────────────────────────────────────────────────────────

type PreflightStatus = 'Valid' | 'Warning' | 'Failed';
type DensityMode = 'compact' | 'comfortable';
type GroupBy = 'severity' | 'org' | 'module';

interface PreflightEntry {
  rowNumber: number;
  moduleKey: string;
  orgName: string;
  status: PreflightStatus;
  message: string | null;
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: PreflightStatus }) {
  const classes =
    status === 'Valid'
      ? 'border-success/50 bg-success/10 text-success'
      : status === 'Warning'
        ? 'border-warning/50 bg-warning/10 text-warning'
        : 'border-destructive/50 bg-destructive/10 text-destructive';

  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', classes)}>
      {status}
    </span>
  );
}

// ─── Compact heatmap cell ─────────────────────────────────────────────────────

interface HeatmapCellProps {
  entry: PreflightEntry;
}

function HeatmapCell({ entry }: HeatmapCellProps) {
  const colorClass =
    entry.status === 'Valid'
      ? 'bg-success/70'
      : entry.status === 'Warning'
        ? 'bg-warning/70'
        : 'bg-destructive/70';

  const tooltip = `${entry.orgName} · ${entry.moduleKey} · ${entry.status}${entry.message !== null ? ` — ${entry.message}` : ''}`;

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      className={cn('block h-5 w-5 rounded-sm cursor-default', colorClass)}
    />
  );
}

// ─── Collapsible group section ────────────────────────────────────────────────

interface CollapsibleGroupProps {
  label: string;
  count: number;
  children: React.ReactNode;
  defaultOpen?: boolean | undefined;
}

function CollapsibleGroup({ label, count, children, defaultOpen = true }: CollapsibleGroupProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between bg-muted px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/80"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span>
          {label}
          <span className="ml-2 rounded-full bg-border px-2 py-0.5 text-xs text-muted-foreground">
            {count}
          </span>
        </span>
        <span className="text-muted-foreground text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

// ─── CSV export helper ────────────────────────────────────────────────────────

function downloadPreflightFailuresCsv(entries: PreflightEntry[]): void {
  const failed = entries.filter((e) => e.status === 'Failed');
  const header = 'rowNumber,orgName,moduleKey,status,message\n';
  const rows = failed
    .map(
      (e) =>
        `${e.rowNumber},"${e.orgName.replace(/"/g, '""')}",${e.moduleKey},${e.status},"${(e.message ?? '').replace(/"/g, '""')}"`,
    )
    .join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'preflight-failures.csv';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// ─── PreflightStep ────────────────────────────────────────────────────────────

export function PreflightStep() {
  const parsedCsv = useMassMigrationWizardParsedCsv();
  const idValidationResults = useMassMigrationWizardIdValidation();
  const excludedRows = useMassMigrationWizardExcludedRows();
  const selectionsByRow = useMassMigrationWizardSelections();
  const moduleValidationResults = useMassMigrationWizardModuleValidation();
  const detectedByRow = useMassMigrationWizardDetectedModules();
  const { setModuleValidationResults, setStep } = useMassMigrationWizardActions();
  const { run, isRunning, progress, error } = usePreflightValidation();

  const [density, setDensity] = useState<DensityMode>('comfortable');
  const [groupBy, setGroupBy] = useState<GroupBy>('severity');

  // Org name lookup
  const orgNameByRow = useMemo(
    () =>
      new Map(
        (parsedCsv?.rows ?? []).map((r) => [r.rowNumber, r.orgName ?? r.bridgeId]),
      ),
    [parsedCsv],
  );

  // Active row numbers
  const activeRowNumbers = useMemo(
    () =>
      (parsedCsv?.rows ?? [])
        .filter((r) => !excludedRows.includes(r.rowNumber))
        .map((r) => r.rowNumber),
    [parsedCsv, excludedRows],
  );

  // Real validation: ID-validation failures and unmatched modules fail locally; every
  // other (row, module) pair is validated against its source system through the same
  // endpoint the single wizard uses. The orchestrator validates again at execution time,
  // so this is operator feedback, not the contract.
  const runValidation = useCallback(async () => {
    const entries: PreflightEntry[] = [];
    const pending: Array<PreflightEntry & { sourceId: string }> = [];

    activeRowNumbers.forEach((rn) => {
      const orgName = orgNameByRow.get(rn) ?? `Row ${rn}`;
      const idResult = idValidationResults.find((r) => r.rowNumber === rn);

      (selectionsByRow[rn] ?? []).forEach((moduleKey) => {
        if (idResult?.bridgeStatus === 'Invalid') {
          entries.push({ rowNumber: rn, moduleKey, orgName, status: 'Failed', message: idResult.bridgeIssue });
          return;
        }
        const sourceId = detectedByRow[rn]?.find((m) => m.moduleKey === moduleKey)?.sourceId;
        if (sourceId === undefined || sourceId === '') {
          entries.push({
            rowNumber: rn,
            moduleKey,
            orgName,
            status: 'Failed',
            message: 'No Bridge product matched this module — go back to Module Selection and re-run detection',
          });
          return;
        }
        pending.push({ rowNumber: rn, moduleKey, orgName, status: 'Valid', message: null, sourceId });
      });
    });

    const results = await run(pending.map(({ moduleKey, sourceId }) => ({ moduleKey, sourceId })));
    if (results === null) return; // request failed — the hook surfaces the message

    pending.forEach((item) => {
      const result = results.get(preflightKey(item.moduleKey, item.sourceId));
      const idResult = idValidationResults.find((r) => r.rowNumber === item.rowNumber);
      let status: PreflightStatus;
      let message: string | null;
      if (result === undefined) {
        status = 'Failed';
        message = 'No validation result returned for this source';
      } else if (!result.isValid) {
        status = 'Failed';
        message = result.blockers.length > 0 ? result.blockers.join('; ') : 'Source failed validation';
      } else if (idResult?.bridgeStatus === 'Warning') {
        status = 'Warning';
        message = idResult.bridgeIssue;
      } else {
        status = 'Valid';
        message = null;
      }
      entries.push({ rowNumber: item.rowNumber, moduleKey: item.moduleKey, orgName: item.orgName, status, message });
    });

    setModuleValidationResults(
      entries.map((e) => ({ rowNumber: e.rowNumber, moduleKey: e.moduleKey, status: e.status, issue: e.message })),
    );
  }, [activeRowNumbers, selectionsByRow, idValidationResults, orgNameByRow, detectedByRow, run, setModuleValidationResults]);

  const initialized = useRef(false);

  // Run on first visit and whenever the store has no results — the store clears them when rows
  // are excluded or module selections change, so stale all-valid results cannot enable Next.
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (moduleValidationResults.length === 0) {
      void runValidation();
    }
  }, [moduleValidationResults.length, runValidation]);

  // Derive display entries from store results
  const preflightEntries = useMemo<PreflightEntry[]>(() => {
    return moduleValidationResults.map((r) => ({
      rowNumber: r.rowNumber,
      moduleKey: r.moduleKey,
      orgName: orgNameByRow.get(r.rowNumber) ?? `Row ${r.rowNumber}`,
      status: (r.status as PreflightStatus) ?? 'Valid',
      message: r.issue,
    }));
  }, [moduleValidationResults, orgNameByRow]);

  // Stats
  const totalChecks = preflightEntries.length;
  const validCount = preflightEntries.filter((e) => e.status === 'Valid').length;
  const warningCount = preflightEntries.filter((e) => e.status === 'Warning').length;
  const failedCount = preflightEntries.filter((e) => e.status === 'Failed').length;

  // Group entries for comfortable view
  const groupedEntries = useMemo(() => {
    if (groupBy === 'severity') {
      const order: PreflightStatus[] = ['Failed', 'Warning', 'Valid'];
      return order.map((s) => ({
        label: s,
        entries: preflightEntries.filter((e) => e.status === s),
      })).filter((g) => g.entries.length > 0);
    }

    if (groupBy === 'org') {
      const orgMap = new Map<string, PreflightEntry[]>();
      preflightEntries.forEach((e) => {
        const key = e.orgName;
        const existing = orgMap.get(key) ?? [];
        existing.push(e);
        orgMap.set(key, existing);
      });
      return [...orgMap.entries()].map(([label, entries]) => ({ label, entries }));
    }

    // groupBy === 'module'
    const modMap = new Map<string, PreflightEntry[]>();
    preflightEntries.forEach((e) => {
      const existing = modMap.get(e.moduleKey) ?? [];
      existing.push(e);
      modMap.set(e.moduleKey, existing);
    });
    return [...modMap.entries()].map(([label, entries]) => ({ label, entries }));
  }, [preflightEntries, groupBy]);

  const hasResults = preflightEntries.length > 0;

  return (
    <div className="space-y-6">
      {isRunning && (
        <Alert role="status" aria-live="polite">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <AlertTitle>Validating sources…</AlertTitle>
          <AlertDescription>
            {progress.done} of {progress.total} module sources checked against their source systems.
          </AlertDescription>
        </Alert>
      )}

      {error !== null && !isRunning && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Validation could not complete</AlertTitle>
          <AlertDescription>{error} Re-run validation to try again.</AlertDescription>
        </Alert>
      )}

      {/* Stat strip */}
      <div className="grid grid-cols-4 gap-3">
        <StatTile label="Total checks" value={totalChecks} variant="default" />
        <StatTile label="Valid" value={validCount} variant="success" />
        <StatTile label="Warnings" value={warningCount} variant="warning" />
        <StatTile label="Failed" value={failedCount} variant="destructive" />
      </div>

      {/* Blocking alert */}
      {failedCount > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{failedCount} validation failure{failedCount !== 1 ? 's' : ''} must be resolved before proceeding.</AlertTitle>
          <AlertDescription>
            Fix the source data or go back and exclude the failing organizations, then re-run validation.
          </AlertDescription>
        </Alert>
      )}

      {/* Controls row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Density toggle */}
        <div className="flex items-center gap-1 rounded-full border border-border bg-muted p-0.5 w-fit">
          {(['compact', 'comfortable'] as DensityMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setDensity(mode)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors capitalize',
                density === mode
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              aria-pressed={density === mode}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => downloadPreflightFailuresCsv(preflightEntries)}
            disabled={failedCount === 0}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export failures
          </Button>
          <Button variant="outline" size="sm" type="button" onClick={() => void runValidation()} disabled={isRunning}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Re-run validation
          </Button>
        </div>
      </div>

      {/* Results */}
      {hasResults && (
        <>
          {density === 'compact' ? (
            /* Heatmap grid */
            <div className="rounded-md border border-border p-4">
              <p className="mb-3 text-xs text-muted-foreground">
                Each square represents one (org, module) pair. Hover for details.
              </p>
              <div className="flex flex-wrap gap-1">
                {preflightEntries.map((entry) => (
                  <HeatmapCell
                    key={`${entry.rowNumber}-${entry.moduleKey}`}
                    entry={entry}
                  />
                ))}
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded-sm bg-success/70" />
                  Valid
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded-sm bg-warning/70" />
                  Warning
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded-sm bg-destructive/70" />
                  Failed
                </span>
              </div>
            </div>
          ) : (
            /* Comfortable table grouped */
            <div className="space-y-4">
              {/* Group-by selector */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Group by:</span>
                <div className="flex items-center gap-1 rounded-full border border-border bg-muted p-0.5 w-fit">
                  {(['severity', 'org', 'module'] as GroupBy[]).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGroupBy(g)}
                      className={cn(
                        'rounded-full px-3 py-1 text-xs font-medium transition-colors capitalize',
                        groupBy === g
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                      aria-pressed={groupBy === g}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {groupedEntries.map(({ label, entries }) => (
                <CollapsibleGroup
                  key={label}
                  label={label}
                  count={entries.length}
                  defaultOpen={label === 'Failed' || label === 'Warning'}
                >
                  <table className="w-full text-sm" aria-label={label}>
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Org Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Module</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr
                          key={`${entry.rowNumber}-${entry.moduleKey}`}
                          className="border-t border-border bg-background"
                        >
                          <td className="px-3 py-2 text-sm">{entry.orgName}</td>
                          <td className="px-3 py-2 text-sm font-mono text-muted-foreground">{entry.moduleKey}</td>
                          <td className="px-3 py-2">
                            <StatusBadge status={entry.status} />
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            {entry.message ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CollapsibleGroup>
              ))}
            </div>
          )}
        </>
      )}

      {!hasResults && !isRunning && (
        <div className="flex min-h-[200px] items-center justify-center rounded-md border border-dashed border-border">
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">No results yet.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void runValidation()}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Run validation
            </Button>
          </div>
        </div>
      )}

      <WizardNavigation
        onBack={() => setStep(4)}
        onNext={failedCount === 0 && hasResults && !isRunning && error === null ? () => setStep(6) : undefined}
        nextDisabled={failedCount > 0 || !hasResults || isRunning || error !== null}
        nextLabel="Review & Confirm"
      />
    </div>
  );
}
